import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchTournaments } from "../../../hooks/queries/useSuperAdmin";
import { useUpdateTournament } from "../../../hooks/mutations/useSuperAdminMutations";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { formatDate } from "../../../util/StringUtils";
import type { Tournament, TournamentTypeValue } from "../../../types/domain";

const TOURNAMENT_TYPES: TournamentTypeValue[] = ["SWISS", "GROUPS", "GROUPS_PLAYOFF", "BLAST"];

function TournamentSection() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);

  const { data: results, isFetching } = useSearchTournaments(
    { q: debouncedQuery, page: 0, size: 25 },
    debouncedQuery.length > 0,
  );

  return (
    <div>
      <Form.Control
        type="text"
        placeholder={t("zakulisi__searchTournament")}
        value={query}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
        className="mb-3"
      />
      {isFetching && <Spinner animation="border" size="sm" className="ms-2" />}
      {results && results.content.length > 0 && (
        <Table striped bordered hover size="sm" variant="dark">
          <thead>
            <tr>
              <th style={{ width: "5rem" }}>ID</th>
              <th>{t("tournamentName")}</th>
              <th style={{ width: "6rem" }}>{t("zakulisi__type")}</th>
              <th style={{ width: "5rem" }}>{t("zakulisi__sets")}</th>
              <th style={{ width: "8rem" }}>{t("organizer")}</th>
              <th style={{ width: "7rem" }}>{t("zakulisi__state")}</th>
              <th style={{ width: "6rem" }}>{t("zakulisi__date")}</th>
              <th style={{ width: "5rem" }}>{t("auditLog__action")}</th>
            </tr>
          </thead>
          <tbody>
            {results.content.map((tournament) => (
              <tr key={tournament.id}>
                <td>{tournament.id}</td>
                <td>{tournament.name}</td>
                <td>{tournament.type}</td>
                <td>{tournament.setsNeededToWin}</td>
                <td>{tournament.organizer}</td>
                <td>{tournament.state}</td>
                <td>{formatDate(tournament.createdAt)}</td>
                <td>
                  <Button variant="outline-info" size="sm" onClick={() => setEditingTournament(tournament)}>
                    {t("zakulisi__edit")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {debouncedQuery && results && results.content.length === 0 && !isFetching && (
        <div className="text-center" style={{ color: "var(--text-muted)" }}>{t("auditLog__empty")}</div>
      )}

      <EditTournamentModal
        tournament={editingTournament}
        onClose={() => setEditingTournament(null)}
      />
    </div>
  );
}

interface EditTournamentModalProps {
  tournament: Tournament | null;
  onClose: () => void;
}

function EditTournamentModal({ tournament, onClose }: EditTournamentModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [organizer, setOrganizer] = useState("");
  const [setsNeededToWin, setSetsNeededToWin] = useState(2);
  const [type, setType] = useState<TournamentTypeValue>("SWISS");
  const [hidden, setHidden] = useState(false);
  const updateMutation = useUpdateTournament();

  // Re-seed form state whenever a new tournament is opened for editing.
  useEffect(() => {
    if (tournament) {
      setName(tournament.name);
      setOrganizer(tournament.organizer);
      setSetsNeededToWin(tournament.setsNeededToWin);
      setType(tournament.type);
      setHidden(tournament.hidden);
    }
  }, [tournament]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!tournament) return;
    await updateMutation.mutateAsync({
      tournamentId: tournament.id,
      update: { name, organizer, setsNeededToWin, type, hidden },
    });
    onClose();
  };

  return (
    <Modal show={tournament != null} onHide={onClose} centered>
      <Modal.Header closeButton style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}>
        <Modal.Title>
          {t("zakulisi__editing")}: {tournament && <>#{tournament.id} — {tournament.name}</>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" }}>
        <Form id="edit-tournament-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-2">
            <Form.Label>{t("tournamentName")}</Form.Label>
            <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>{t("organizer")}</Form.Label>
            <Form.Control type="text" value={organizer} onChange={(e) => setOrganizer(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>{t("zakulisi__sets")}</Form.Label>
            <Form.Control type="number" min="1" max="9" value={setsNeededToWin} onChange={(e) => setSetsNeededToWin(Number(e.target.value))} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("zakulisi__type")}</Form.Label>
            <Form.Select value={type} onChange={(e) => setType(e.target.value as TournamentTypeValue)}>
              {TOURNAMENT_TYPES.map((tt) => (
                <option key={tt} value={tt}>{tt}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="edit-tournament-hidden"
              label={t("zakulisi__hidden")}
              checked={hidden}
              onChange={(e) => setHidden(e.target.checked)}
            />
            <Form.Text style={{ color: "var(--text-muted)" }}>
              {t("zakulisi__hiddenHelp")}
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
        <Button variant="outline-secondary" onClick={onClose}>{t("cancel")}</Button>
        <Button type="submit" form="edit-tournament-form" variant="success" disabled={updateMutation.isPending}>
          {t("save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TournamentSection;
